import {
  AvailableEnclosure,
  EnclosureProposition,
  EnclosurePropositionResponse,
  Project,
  ProjectForList,
} from '../models';

type EnclosurePropositionLike =
  | EnclosureProposition
  | EnclosurePropositionResponse;

function toArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }

  const relation = value as { data?: unknown };
  if (Array.isArray(relation?.data)) {
    return relation.data as T[];
  }

  return [];
}

function normalizeEnclosureStatus(
  proposition: EnclosurePropositionLike,
  index: number,
): EnclosureProposition {
  const enclosureStatus =
    (proposition as EnclosurePropositionResponse).enclosureStatus ??
    (proposition as EnclosurePropositionResponse).status ??
    proposition.enclosureStatus;

  const propositions = toArray<AvailableEnclosure>(
    proposition.propositions,
  ).map((item) => item);

  return {
    ...proposition,
    enclosureStatus: (enclosureStatus ??
      'feasible') as EnclosureProposition['enclosureStatus'],
    propositions,
    temporaryId: (proposition as EnclosureProposition).temporaryId ?? index + 1,
  };
}

export function getSelectedEnclosureProposition(
  project?: Project | ProjectForList | null,
): EnclosureProposition | undefined {
  const propositions = toArray<EnclosurePropositionLike>(
    project?.enclosurePropositions,
  ).map(normalizeEnclosureStatus);

  if (!propositions.length) {
    return undefined;
  }

  return (
    propositions.find(
      (proposition) =>
        (proposition.enclosureStatus ?? '').toLowerCase() === 'selected',
    ) ?? propositions[0]
  );
}

export function getSelectedAvailableEnclosure(
  project?: Project | ProjectForList | null,
): AvailableEnclosure | undefined {
  const selectedProposition = getSelectedEnclosureProposition(project);
  if (!selectedProposition?.propositions?.length) {
    return undefined;
  }

  return selectedProposition.propositions.find(
    (proposition) => proposition.cabinet || proposition.rack,
  );
}

export function getSelectedEnclosureSummary(
  project?: Project | ProjectForList | null,
): { type: 'Cabinet' | 'Rack'; reference: string } | null {
  const directType = project?.selectedEnclosureType;
  const directReference = project?.selectedEnclosureReference?.trim();
  if (directType && directReference) {
    return { type: directType, reference: directReference };
  }

  const enclosure = getSelectedAvailableEnclosure(project);
  const cabinetReference = enclosure?.cabinet?.reference?.trim();
  if (cabinetReference) {
    return { type: 'Cabinet', reference: cabinetReference };
  }

  const rackReference = enclosure?.rack?.reference?.trim();
  if (rackReference) {
    return { type: 'Rack', reference: rackReference };
  }

  return null;
}
